import { useState } from 'react';
import type { ClientRole } from '@zcs/shared';
import {
  canGrantParticipant,
  displayNameForUserId,
  type MeetingParticipantsState,
} from './meetingParticipants';

interface ControlRosterProps {
  selfUserId: string;
  participantsState: MeetingParticipantsState;
  grantedUserIds: string[];
  onGrant: (userId: string) => void;
  onRevoke: (userId: string) => void;
}

function roleLabel(role: ClientRole): string {
  if (role === 'host') return 'Host';
  if (role === 'co-host') return 'Co-host';
  return 'Participant';
}

export function ControlRoster({
  selfUserId,
  participantsState,
  grantedUserIds,
  onGrant,
  onRevoke,
}: ControlRosterProps) {
  const [manualId, setManualId] = useState('');
  const [showManual, setShowManual] = useState(false);

  const grantedSet = new Set(grantedUserIds);
  const participants =
    participantsState.status === 'ready' ? participantsState.participants : [];

  const grantable = participants.filter((p) => canGrantParticipant(p, selfUserId));
  const othersInMeeting = participants.filter((p) => p.participantUUID !== selfUserId);
  const grantedNotInList = grantedUserIds.filter(
    (id) => !participants.some((p) => p.participantUUID === id),
  );

  const emptyRoster =
    participantsState.status === 'ready' &&
    grantable.length === 0 &&
    grantedUserIds.length === 0;

  return (
    <section className="roster">
      <h2>Manage Control Access</h2>
      <p className="roster-hint">
        Grant switching access to participants in this meeting. Only hosts and co-hosts can change
        access.
      </p>

      {participantsState.status === 'loading' && (
        <p className="roster-status">Loading participants…</p>
      )}
      {participantsState.status === 'error' && (
        <p className="roster-status roster-status-error">{participantsState.message}</p>
      )}

      {emptyRoster && (
        <p className="roster-status">
          {othersInMeeting.length === 0
            ? 'No other participants appear in the app roster yet. Zoom often only lists people who have opened this app in the meeting — ask them to open Companion Switcher, then check again.'
            : 'Everyone currently listed already has host or co-host access.'}
        </p>
      )}

      {grantable.length > 0 && (
        <ul className="roster-list roster-list-participants">
          {grantable.map((p) => {
            const granted = grantedSet.has(p.participantUUID);
            return (
              <li key={p.participantUUID}>
                <div className="roster-participant-info">
                  <span className="roster-participant-name">{p.screenName}</span>
                  <span className="roster-participant-role">{roleLabel(p.role)}</span>
                </div>
                {granted ? (
                  <button type="button" onClick={() => onRevoke(p.participantUUID)}>
                    Revoke
                  </button>
                ) : (
                  <button type="button" onClick={() => onGrant(p.participantUUID)}>
                    Grant
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {grantedNotInList.length > 0 && (
        <>
          <h3 className="roster-subheading">Granted (left meeting)</h3>
          <ul className="roster-list">
            {grantedNotInList.map((id) => (
              <li key={id}>
                <span>{displayNameForUserId(id, participants)}</span>
                <button type="button" onClick={() => onRevoke(id)}>
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="roster-manual">
        <button
          type="button"
          className="roster-manual-toggle"
          onClick={() => setShowManual((v) => !v)}
          aria-expanded={showManual}
        >
          {showManual ? 'Hide manual grant' : 'Grant by participant ID'}
        </button>
        {showManual && (
          <div className="roster-manual-form">
            <p className="roster-manual-hint">
              Use when someone opened this app but does not appear above. They can copy their ID from
              the app after opening it (must match the meeting).
            </p>
            <input
              type="text"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="Participant UUID"
              aria-label="Participant UUID"
            />
            <button
              type="button"
              disabled={!manualId.trim()}
              onClick={() => {
                onGrant(manualId.trim());
                setManualId('');
              }}
            >
              Grant
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
